import { useState } from "react";
import { ReportView } from "./ReportView";
import { useAbcXyzMatrix, type AbcXyzMatrix } from "@/lib/api/reports";
import { dateTime, relative } from "@/lib/format";
import "./abc-xyz.css";

const ABC_ROWS = [
  { id: "A", label: "ko'p teriladi" },
  { id: "B", label: "o'rtacha" },
  { id: "C", label: "kam teriladi" },
] as const;

const XYZ_COLUMNS = [
  { id: "X", label: "barqaror" },
  { id: "Y", label: "o'zgaruvchan" },
  { id: "Z", label: "tartibsiz" },
  { id: "NONE", label: "klass yo'q" },
] as const;

interface Selection {
  abc?: string;
  xyz?: string;
}

/** Ulushni foizga: "0.25" → "25%". */
function percent(value: string | undefined): string {
  const number = Number(value);
  return Number.isFinite(number) ? `${Math.round(number * 1000) / 10}%` : "—";
}

function countOf(
  matrix: AbcXyzMatrix | undefined,
  abc: string,
  xyz: string,
): number {
  return (matrix?.cells ?? [])
    .filter((cell) => cell.abc_class === abc && cell.xyz_class === xyz)
    .reduce((sum, cell) => sum + cell.sku_count, 0);
}

/**
 * ABC × XYZ matritsasi.
 *
 * Katak rangi — BITTA tusning to'yinganligi (miqdor), matn esa
 * doim asosiy siyoh: son katakda yozilgan, rang faqat ko'zni eng
 * katta katakka yo'naltiradi. To'yinganlik 45% dan oshmaydi —
 * undan keyin qora matn o'qilmay qolardi.
 */
function Matrix({
  matrix,
  selection,
  onSelect,
}: {
  matrix: AbcXyzMatrix | undefined;
  selection: Selection;
  onSelect: (next: Selection) => void;
}) {
  const max = Math.max(
    1,
    ...(matrix?.cells ?? []).map((cell) => cell.sku_count),
  );
  const rule = matrix?.rule;

  return (
    <section className="card abcxyz" aria-label="ABC va XYZ matritsasi">
      <div className="abcxyz-scroll">
        <table className="abcxyz-grid">
          <thead>
            <tr>
              <th scope="col" className="abcxyz-corner">
                ABC \ XYZ
              </th>
              {XYZ_COLUMNS.map((column) => (
                <th key={column.id} scope="col">
                  <button
                    type="button"
                    className="abcxyz-axis"
                    aria-pressed={selection.xyz === column.id && !selection.abc}
                    onClick={() => onSelect({ xyz: column.id })}
                  >
                    <strong>{column.id === "NONE" ? "—" : column.id}</strong>
                    <span>{column.label}</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ABC_ROWS.map((row) => (
              <tr key={row.id}>
                <th scope="row">
                  <button
                    type="button"
                    className="abcxyz-axis"
                    aria-pressed={selection.abc === row.id && !selection.xyz}
                    onClick={() => onSelect({ abc: row.id })}
                  >
                    <strong>{row.id}</strong>
                    <span>{row.label}</span>
                  </button>
                </th>
                {XYZ_COLUMNS.map((column) => {
                  const count = countOf(matrix, row.id, column.id);
                  const active =
                    selection.abc === row.id && selection.xyz === column.id;
                  const strength =
                    count === 0 ? 0 : 8 + Math.round((count / max) * 37);
                  return (
                    <td key={column.id}>
                      <button
                        type="button"
                        className="abcxyz-cell"
                        aria-pressed={active}
                        title={`${row.id}${column.id === "NONE" ? "" : column.id}: ${count} ta SKU`}
                        style={{
                          background:
                            strength > 0
                              ? `color-mix(in srgb, var(--primary) ${strength}%, var(--surface-container-lowest))`
                              : undefined,
                        }}
                        onClick={() =>
                          onSelect(
                            active ? {} : { abc: row.id, xyz: column.id },
                          )
                        }
                      >
                        <span className="abcxyz-code">
                          {row.id}
                          {column.id === "NONE" ? "—" : column.id}
                        </span>
                        <span
                          className={
                            count === 0
                              ? "abcxyz-count is-empty"
                              : "abcxyz-count"
                          }
                        >
                          {count}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="abcxyz-rule">
        {rule ? (
          <>
            ABC: {rule.abc_window_days} kun, chegara{" "}
            {percent(rule.abc_thresholds.a)} / {percent(rule.abc_thresholds.b)}{" "}
            · XYZ: {rule.xyz_periods} × {rule.xyz_period_days} kun, σ/μ ≤{" "}
            {percent(rule.xyz_thresholds.x)} — X, ≤{" "}
            {percent(rule.xyz_thresholds.y)} — Y · hisoblandi{" "}
            <span title={dateTime(rule.run_at)}>{relative(rule.run_at)}</span>
          </>
        ) : (
          "Tasnif hali hisoblanmagan — sutkalik hisoblashdan keyin to'ladi."
        )}
      </p>
    </section>
  );
}

/**
 * ABC/XYZ hisoboti (TZ 3.0 P04-02).
 *
 * Biznes qarori (29.09.2026): XYZ — HISOBOT. U slotting va min/max
 * ga ta'sir qilmaydi; ABC esa joylashtirishda ishlatiladi.
 */
export function AbcXyzView({ hint }: { hint: string }) {
  const matrix = useAbcXyzMatrix();
  const [selection, setSelection] = useState<Selection>({});

  return (
    <ReportView
      report="abc-xyz"
      title="ABC / XYZ tasnifi"
      hint={hint}
      filters={{ abc_class: selection.abc, xyz_class: selection.xyz }}
      above={
        <Matrix
          matrix={matrix.data}
          selection={selection}
          onSelect={setSelection}
        />
      }
    />
  );
}
